import { IconChevronLeft, IconDownload, IconLoader2 } from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEffect, useRef, useState } from 'react';
import { SearchParam, TableType } from '@common/ts';
import { getErrorMessage } from '@common/utils';
import { Button } from '@/components/ui/button';
import { useCurrentConversations } from '@/hooks/useCurrentConversations';
import { useTranscriptNavigation } from '@/hooks/useTranscriptNavigation';
import { getSortedTranslatedLanguages } from '@/utils/getSortedTranslatedLanguages';
import { useMutateGenerateTranscriptsTranslation } from '@/reactQuery/post';
import useTranslation from '@/hooks/useTranslation';
import TranscriptsTranslationDropdown from './transcripts_table_data/TranscriptsTranslationDropdown';
import languagesList from '@/lang/languages/languagesList';
import ConfirmTranslationModal from '@/components/Modals/ConfirmTranslationModal';
import { TranscriptProps } from '@/types';
import { ConversationNavigationType, TranscriptsView } from '@/enums';
import useTranslationsLoadingStore from '@/store/TranslationLoadingStore';
import ExportModal from '@/components/Modals/ExportModal/ExportModal';
import { useMutateGetConversationsPageWithStore, useGetConversation } from '@/reactQuery/get';
import NavigationControls from '@/components/NavigationControls/NavigationControls';
import ConversationChat from '@/components/ConversationChat/ConversationChat';
import ConversationMetadata from '@/components/ConversationMetadata/ConversationMetadata';
import { getConversationId } from '@/utils/getTranscriptId';
import { getPaginationParamPage } from '@/utils/urlParams';

const MAX_SUMMARY = 80;

function SingleTranscriptData({
  completedConversationsLength,
  languages,
  projectId,
  initialTranslatedLanguages,
  onScrollTo,
  urlCurrentPage,
  tableType,
  activeView,
}: TranscriptProps) {
  const { lang } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const translationLanguage = searchParams.get(SearchParam.TRANSLATION_LANGUAGE);
  const conversationFromQuestionsTab =
    searchParams.get(SearchParam.CONVERSATION_FROM_QUESTIONS_TAB) === 'true';
  const withoutNavigation = conversationFromQuestionsTab;
  const activeLanguage = translationLanguage || initialTranslatedLanguages[0];
  const [dropdownLanguage, setDropdownLanguage] = useState<string | null>(null);
  const [translatedLanguages] = useState<string[]>(initialTranslatedLanguages);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unTranslatedLanguages] = useState<string[]>(
    languagesList.language
      .map(({ label }) => label)
      .filter((language) => !languages.includes(language) && language !== activeLanguage),
  );
  const isCompletedTranscriptsView = activeView === TranscriptsView.COMPLETED;

  // Use navigation hook
  const {
    conversationId,
    isFirstConversation,
    isLastConversation,
    isGetConversationsPagePending,
    removeSelectedTranscriptAndGoBack,
    handleNextTranscript,
    handlePrevTranscript,
    direction,
  } = useTranscriptNavigation({
    projectId: projectId!,
    activeView,
    urlCurrentPage,
    tableType,
    onScrollTo,
  });

  const {
    translationsLoading,
    setTranslationsLoading,
    setTranslating,
    setTranslatingProjects,
    translatingProjects,
  } = useTranslationsLoadingStore();

  const isEnabledFetch = useRef(true);
  const { mutateAsync } = useMutateGenerateTranscriptsTranslation(projectId);
  const { mutateAsync: mutateGetConversationsPage } = useMutateGetConversationsPageWithStore(
    isCompletedTranscriptsView,
    urlCurrentPage,
    true,
  );
  const {
    selectedTranscript,
    actions: { setSelectedTranscript },
    utils: { getTranscriptById },
  } = useCurrentConversations();
  // Get transcript from store first
  const storeTranscript = getTranscriptById(conversationId);

  // Only fetch from API if coming from questions tab AND not already in store
  const shouldFetchFromApi = conversationFromQuestionsTab || !storeTranscript;

  // Get conversation from API when coming from questions tab
  const { data: conversationFromApi } = useGetConversation(
    projectId!,
    shouldFetchFromApi ? conversationId : '',
  );
  // Use store data if available, otherwise use API data when coming from questions tab
  const effectiveTranscript = storeTranscript || conversationFromApi;

  // Set selected transcript when conversationId changes
  useEffect(() => {
    if (conversationId && effectiveTranscript) {
      setSelectedTranscript(effectiveTranscript);
    }
  }, [conversationId, effectiveTranscript, setSelectedTranscript, translationLanguage]);

  const currentProjectIsTranslating =
    translatingProjects?.find((project) => project.projectId === projectId) || null;

  const handleLanguageSelection = async (language: string) => {
    if (translatedLanguages.includes(language) || language === initialTranslatedLanguages[0]) {
      const newSearchParams = new URLSearchParams(searchParams);

      if (language === initialTranslatedLanguages[0]) {
        newSearchParams.delete(SearchParam.TRANSLATION_LANGUAGE);
      } else {
        newSearchParams.set(SearchParam.TRANSLATION_LANGUAGE, language);
      }

      setSearchParams(newSearchParams);

      // Fetch conversations with the new language parameter
      try {
        const targetLanguage = language === initialTranslatedLanguages[0] ? null : language;
        const result = await mutateGetConversationsPage({
          projectId: projectId!,
          filterId: searchParams.get(SearchParam.FILTER_ID),
          language: targetLanguage,
          conversationFilter: activeView,
          page: urlCurrentPage,
        });

        const sourceTable = targetLanguage
          ? TableType.TRANSCRIPTS
          : TableType.TRANSLATED_TRANSCRIPTS;
        const targetTable = targetLanguage
          ? TableType.TRANSLATED_TRANSCRIPTS
          : TableType.TRANSCRIPTS;

        // Get current page for the active table
        const currentPage = searchParams.get(getPaginationParamPage(sourceTable));

        // Update or delete the target pagination param accordingly
        if (currentPage) {
          newSearchParams.set(getPaginationParamPage(targetTable), currentPage);
        } else {
          newSearchParams.delete(getPaginationParamPage(targetTable));
        }

        setSearchParams(newSearchParams);

        if (result.items && result.items.length > 0) {
          const currentTranscript = result.items.find(
            (item) => getConversationId(item) === selectedTranscript?._id,
          );
          if (currentTranscript) {
            setSelectedTranscript(currentTranscript);
          }
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        toast.error(errorMessage);
      }
    } else {
      isEnabledFetch.current = false;
      setDropdownLanguage(language);
      setIsModalOpen(true);
    }
  };

  const confirmTranslation = async () => {
    if (!dropdownLanguage || !completedConversationsLength) return;
    setIsModalOpen(false);
    setTranslating(true);
    toast.info(lang.get('msg.translationMightTakeAWhile'));
    setTranslationsLoading(true);
    setTranslatingProjects([
      ...translatingProjects,
      { projectId, language: dropdownLanguage, length: completedConversationsLength },
    ]);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(SearchParam.SELECTED_TRANSCRIPT);
    newSearchParams.delete(SearchParam.CONVERSATION_FROM_QUESTIONS_TAB);
    newSearchParams.set(SearchParam.TRANSLATION_LANGUAGE, dropdownLanguage);
    setSearchParams(newSearchParams);
    try {
      await mutateAsync({ languages: [dropdownLanguage] });
    } catch (error) {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
      setTranslating(false);
    }
  };

  const isFetchingPrevConversation =
    isGetConversationsPagePending && direction === ConversationNavigationType.PREV;
  const isFetchingNextConversation =
    isGetConversationsPagePending && direction === ConversationNavigationType.NEXT;
  const isPrevDisabled = isFirstConversation;
  const isNextDisabled = isLastConversation;

  return (
    <>
      <div className="flex flex-col w-full">
        <div className="flex items-center justify-between w-full mb-4 pt-4">
          <button
            onClick={removeSelectedTranscriptAndGoBack}
            type="button"
            className="hover:font-semibold flex items-center gap-1.5"
          >
            <IconChevronLeft size={18} className="mt-0.5" />
            {lang.get('msg.goBack')}
          </button>
          <div className="flex items-center gap-4">
            {isCompletedTranscriptsView && (
              <TranscriptsTranslationDropdown
                isLoading={!!currentProjectIsTranslating}
                activeLanguage={activeLanguage! || dropdownLanguage}
                translatedLanguages={getSortedTranslatedLanguages(
                  initialTranslatedLanguages,
                  translatedLanguages,
                )}
                unTranslatedLanguages={unTranslatedLanguages}
                handleLanguageSelection={handleLanguageSelection}
              />
            )}
            {!withoutNavigation && (
              <NavigationControls
                onPrev={handlePrevTranscript}
                onNext={handleNextTranscript}
                isPrevDisabled={isPrevDisabled}
                isNextDisabled={isNextDisabled}
                isPrevLoading={isFetchingPrevConversation}
                isNextLoading={isFetchingNextConversation}
                selectedTranscript={selectedTranscript}
              />
            )}
            {!translationLanguage && (
              <Button
                variant="default"
                onClick={() => setIsExportModalOpen(true)}
                className="flex items-center gap-1 text-sm"
              >
                <IconDownload size={18} />
                {lang.get('msg.export')}
              </Button>
            )}
          </div>
        </div>
        {!translationsLoading && selectedTranscript && (
          <div className="flex w-full gap-5 md800:flex-col-reverse max-h-[calc(100dvh-242px)] overflow-hidden">
            <ConversationChat messages={selectedTranscript?.messages} />
            <ConversationMetadata
              conversation={selectedTranscript}
              totalMessages={selectedTranscript?.messages?.length}
              isExpanded={isExpanded}
              onSummaryToggle={setIsExpanded}
              maxSummaryWords={MAX_SUMMARY}
            />
          </div>
        )}
        {(translationsLoading || !selectedTranscript) && (
          <div className="flex items-center justify-center w-full h-full min-h-[600px]">
            <IconLoader2 className="animate-spin" size={30} />
          </div>
        )}
      </div>
      <ConfirmTranslationModal
        isOpen={isModalOpen}
        onConfirm={confirmTranslation}
        onCancel={async () => {
          setIsModalOpen(false);
          isEnabledFetch.current = true;
          const newSearchParams = new URLSearchParams(searchParams.toString());
          if (activeLanguage === 'Original') {
            newSearchParams.delete(SearchParam.TRANSLATION_LANGUAGE);
          } else {
            newSearchParams.set(SearchParam.TRANSLATION_LANGUAGE, activeLanguage);
          }
          setSearchParams(newSearchParams);
          setDropdownLanguage(activeLanguage);
        }}
      />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        projectId={projectId!}
        transcriptsView={activeView}
      />
    </>
  );
}

export default SingleTranscriptData;
